/**
 * LEAVE-APPROVED-VIEW-001 — "Approved Leaves" tab for Leave Approvals.
 * Read-only history over existing leave_requests via /api/leave-registry/reports/requests
 * (status=approved, view=approvals). Scope is enforced server-side (Admin/HR → all, TL → own team).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Calendar, Eye, X, CheckCircle, AlertCircle, Ban, UserPlus } from 'lucide-react';
import { formatCalendarDate, formatDateOnly, formatDateTimeLocal } from '../../../utils/formatters';
import { FilterBar, PaginationFooter, TableSkeleton, Avatar, applyFilters, paginate, DEFAULT_TAB_STATE } from './ApprovalsToolkit';

const API = process.env.REACT_APP_BACKEND_URL || '';
const TYPE_LABELS = { casual: 'Casual', sick: 'Sick', planned: 'Planned', emergency: 'Emergency', earned: 'Earned', compensatory: 'Compensatory', maternity: 'Maternity', unpaid: 'Loss of Pay' };
const typeLabel = (t) => TYPE_LABELS[t] || (t || 'Leave').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const range = (s, e) => (s === e ? formatCalendarDate(s) : `${formatCalendarDate(s)} – ${formatCalendarDate(e)}`);
const dayPart = (dp) => (!dp || dp === 'full' ? 'Full Day' : dp === 'half_am' ? 'Half Day — Morning' : 'Half Day — Afternoon');

const StatusCell = ({ r }) => r.cancellation_requested
  ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800" title={`Cancellation requested ${formatDateTimeLocal(r.cancellation_requested_at)}`} data-testid="approved-status-cancel-requested"><Ban className="w-3 h-3" />Cancellation requested</span>
  : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-100 text-emerald-800" data-testid="approved-status-approved"><CheckCircle className="w-3 h-3" />Approved</span>;

const ApprovedBy = ({ r }) => (
  <span className="leading-tight">
    <span className="block text-slate-800">{r.approved_by || '—'}</span>
    {r.hr_approved_by && <span className="block text-[11px] text-slate-500">+ HR: {r.hr_approved_by}</span>}
  </span>
);

const Details = ({ r, onClose }) => (
  <div className="fixed inset-0 z-50" data-testid="approved-details-drawer">
    <div className="absolute inset-0 bg-black/40" onClick={onClose} />
    <div className="absolute right-0 top-0 h-full w-full max-w-[440px] bg-white shadow-2xl overflow-y-auto">
      <div className="sticky top-0 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
        <div><h4 className="text-base font-semibold text-slate-900">{r.employee}</h4><p className="text-xs text-slate-500">Approved Leave{r.submitted_on_behalf ? ' · submitted on behalf' : ''}</p></div>
        <button onClick={onClose} data-testid="approved-details-close" className="p-2 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-5 space-y-4 text-sm">
        <div className="flex flex-wrap gap-2"><span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{typeLabel(r.leave_type)}</span><StatusCell r={r} />{r.submitted_on_behalf && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-100 text-violet-800"><UserPlus className="w-3 h-3" />On Behalf</span>}</div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-400 mb-0.5">Leave Dates</p><p className="font-medium text-slate-800 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-slate-400" />{range(r.start_date, r.end_date)}</p></div>
          <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-400 mb-0.5">Days · Day Part</p><p className="font-medium text-slate-800">{r.days} · {dayPart(r.day_part)}</p></div>
          <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-400 mb-0.5">Approved By</p><p className="font-medium text-slate-800"><ApprovedBy r={r} /></p></div>
          <div className="bg-slate-50 rounded-lg p-3"><p className="text-xs text-slate-400 mb-0.5">Approved On</p><p className="font-medium text-slate-800">{r.approved_on ? formatDateTimeLocal(r.approved_on) : '—'}</p></div>
        </div>
        <div><p className="text-xs text-slate-400 mb-1">Reason</p><p className="text-slate-700 bg-slate-50 rounded-lg p-3 whitespace-pre-wrap" data-testid="approved-details-reason">{r.reason || '— none provided —'}</p></div>
        <p className="text-xs text-slate-500">Applied on {r.applied_at ? formatDateOnly(r.applied_at) : '—'}{r.submitted_on_behalf_by ? ` by ${r.submitted_on_behalf_by} (on behalf)` : ''}.</p>
      </div>
    </div>
  </div>
);

export const ApprovedLeavesTab = ({ onCount }) => {
  // LEAVE-APPROVALS-UI-CONSISTENCY-002: same FilterBar / client-side filters / pagination as the other tabs.
  const [st, setSt] = useState(DEFAULT_TAB_STATE);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const all = []; let page = 1, pages = 1;
      do {
        const res = await axios.get(`${API}/api/leave-registry/reports/requests`, { params: { status: 'approved', view: 'approvals', window: 'all', page, page_size: 200 } });
        all.push(...(res.data.rows || [])); pages = res.data.pages || 1; page += 1;
      } while (page <= pages);
      setRows(all);
      if (onCount) onCount(all.length);
    } catch (e) {
      setError(e.response?.status === 403 ? 'You are not authorized to view approved leave history.' : 'Failed to load approved leaves.');
    } finally { setLoading(false); }
  }, [onCount]);

  useEffect(() => { load(); }, [load]);

  const update = (patch, resetPage = true) => setSt(s => ({ ...s, ...patch, ...(resetPage ? { page: 1 } : {}) }));
  const typeOptions = [...new Set(rows.map(r => r.leave_type).filter(Boolean))].map(t => [t, typeLabel(t)]);
  const statusOptions = [['approved', 'Approved'], ['cancellation_requested', 'Cancellation requested']];
  const filtered = applyFilters(rows, st, {
    getDates: r => [r.start_date, r.end_date],
    getStatus: r => (r.cancellation_requested ? 'cancellation_requested' : 'approved'),
    getText: r => `${r.employee || ''} ${r.email || ''} ${r.reason || ''} ${typeLabel(r.leave_type)} ${r.approved_by || ''} ${r.hr_approved_by || ''}`,
  });
  const { page, totalPages, slice } = paginate(filtered, st);
  const hasFilters = st.search || st.type !== 'all' || st.date !== 'all' || st.status !== 'all';
  const panelKey = `approved-${page}-${st.pageSize}-${st.search}-${st.type}-${st.date}-${st.from}-${st.to}-${st.status}`;

  return (
    <div className="space-y-3" role="tabpanel" id="la-panel-approved" aria-labelledby="la-tab-approved" data-testid="approval-panel-approved">
      <FilterBar tab="approved" st={st} onChange={update} onReset={() => setSt({ ...DEFAULT_TAB_STATE, pageSize: st.pageSize })}
        types={typeOptions.length ? typeOptions : null} statuses={statusOptions}
        searchPlaceholder="Search by employee name, type or reason..." />

      {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-800" data-testid="approved-error"><AlertCircle className="w-4 h-4" />{error}</div>}

      <div key={panelKey} className="la-panel">
        <div className="la-table-wrap overflow-x-auto" data-tone="emerald">
          <table className="la-table w-full" data-testid="approved-leaves-table">
            <thead><tr><th>Employee</th><th>Type</th><th>Dates</th><th>Days</th><th>Reason</th><th>Approved By</th><th>Approved On</th><th>Status</th><th className="text-right">Details</th></tr></thead>
            {loading ? <TableSkeleton cols={9} /> : (
              <tbody>
                {slice.length === 0 && (
                  <tr><td colSpan={9}>
                    <div className="py-10 text-center" data-testid="approval-empty-approved">
                      <CheckCircle className="w-9 h-9 text-emerald-500 mx-auto mb-2" />
                      <p className="text-slate-800 font-medium text-sm">{hasFilters ? 'No approved leaves match your filters' : 'No approved leaves yet'}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{hasFilters ? 'Try adjusting or resetting the filters.' : 'Approved requests will appear here.'}</p>
                    </div>
                  </td></tr>
                )}
                {slice.map(r => (
                  <tr key={r.id} data-testid={`approved-row-${r.id}`}>
                    <td><div className="flex items-center gap-2.5"><Avatar name={r.employee} tone="emerald" /><span className="font-medium text-slate-900">{r.employee}</span>{r.submitted_on_behalf && <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700" title={`Submitted on behalf by ${r.submitted_on_behalf_by || '—'}`}>On behalf</span>}</div></td>
                    <td>{typeLabel(r.leave_type)}</td>
                    <td className="whitespace-nowrap">{range(r.start_date, r.end_date)}</td>
                    <td>{r.days}</td>
                    <td className="max-w-[200px] truncate" title={r.reason}>{r.reason || '—'}</td>
                    <td className="whitespace-nowrap"><ApprovedBy r={r} /></td>
                    <td className="text-slate-500 whitespace-nowrap" data-testid={`approved-on-${r.id}`}>{r.approved_on ? formatDateOnly(r.approved_on) : '—'}</td>
                    <td><StatusCell r={r} /></td>
                    <td><div className="flex justify-end"><button onClick={() => setDetail(r)} data-testid={`approved-view-${r.id}`} title="View Details" className="la-action p-1.5 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100"><Eye className="w-4 h-4" /></button></div></td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      </div>

      <PaginationFooter tab="approved" st={st} total={filtered.length} page={page} totalPages={totalPages} noun="approved leaves" onChange={(patch, resetPage) => update(patch, resetPage)} />
      {detail && <Details r={detail} onClose={() => setDetail(null)} />}
    </div>
  );
};

export default ApprovedLeavesTab;
